/** Formatting helpers kept free of React and DOM dependencies. */

export function formatDate(
	value: string | number | Date,
	locale = 'en-US',
): string {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return '—';

	return new Intl.DateTimeFormat(locale, {
		year: 'numeric',
		month: 'short',
		day: '2-digit',
	}).format(date);
}

/** Date and time, e.g. "Oct 13, 2026, 09:05 AM". `—` for missing values. */
export function formatDateTime(
	value: string | null | undefined,
	locale = 'en-US',
): string {
	if (!value) return '—';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return '—';

	return new Intl.DateTimeFormat(locale, {
		year: 'numeric',
		month: 'short',
		day: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
	}).format(date);
}

/** Elapsed time, e.g. "3 d 4 h", "2 h 15 min", "5 min". */
export function formatDuration(ms: number | null | undefined): string {
	if (ms === null || ms === undefined || ms < 0) return '—';
	const minutes = Math.floor(ms / 60000);
	const hours = Math.floor(minutes / 60);
	const days = Math.floor(hours / 24);

	if (days > 0) return `${days} d ${hours % 24} h`;
	if (hours > 0) return `${hours} h ${minutes % 60} min`;
	return `${minutes} min`;
}

export function formatNumber(value: number, locale = 'en-US'): string {
	return new Intl.NumberFormat(locale).format(value);
}

export function formatCurrency(
	value: number,
	currency = 'USD',
	locale = 'en-US',
): string {
	return new Intl.NumberFormat(locale, {
		style: 'currency',
		currency,
	}).format(value);
}

/**
 * Turn a name or email into initials, e.g. "Jane Doe" -> "JD",
 * "jane.doe@example.com" -> "JD".
 */
export function getInitials(value: string): string {
	const source = value.includes('@') ? value.split('@')[0] : value;

	return source
		.trim()
		.split(/[\s._-]+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((part) => part.charAt(0).toUpperCase())
		.join('');
}

/** Unit and its length in seconds, largest first. */
const RELATIVE_UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
	['day', 24 * 60 * 60],
	['hour', 60 * 60],
	['minute', 60],
];

/** `12 minutes ago`, `3 days ago`; `just now` under a minute. */
export function formatRelativeTime(
	value: string,
	now: number = Date.now(),
	locale = 'en-US',
): string {
	const at = Date.parse(value);
	if (Number.isNaN(at)) return '—';

	const elapsed = Math.round((now - at) / 1000);
	if (elapsed < 60) return 'just now';

	const format = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
	for (const [unit, size] of RELATIVE_UNITS) {
		if (elapsed >= size)
			return format.format(-Math.floor(elapsed / size), unit);
	}
	return 'just now';
}
